import React from 'react';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { isNumber } from 'lodash';
import TabLink from '@dashboard/components/TabLink';
import getConversionRate from '@dashboard/services/getConversionRate';
import { useStatistics } from '../queries';
import { useStatisticsContext } from '../Dashboard';
import { formatTimestamp, formatValue } from '../dates';
import { StatMetric, StatTooltip, StatBody, StatChart } from './StatCard';

function sumArray(array = [], metric) {
  return array.reduce((acc, cur) => acc + (isNumber(cur[metric]) ? cur[metric] : 0), 0);
}

const numberFormatter = value => Intl.NumberFormat('us').format(value).toString();

const percentageFormatter = (value = 0) =>
  `${Intl.NumberFormat('us')
    .format(value * 100)
    .toString()}%`;

export default function Views() {
  const { unit, range } = useStatisticsContext();
  const { data, isLoading: isLoadingVisits } = useStatistics('numberOfVisits');
  const { data: paymentsData, isLoading: isLoadingPayments } = useStatistics('numberOfPayments');
  const { data: bookingsData, isLoading: isLoadingBookings } = useStatistics('numberOfBookings');
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } =
    useStatistics('numberOfSubscriptions');
  const { data: submissionsData, isLoading: isLoadingSubmissions } =
    useStatistics('numberOfSubmissions');

  const isLoading =
    isLoadingVisits ||
    isLoadingPayments ||
    isLoadingBookings ||
    isLoadingSubmissions ||
    isLoadingSubscriptions;

  const totalVisits = data?.reduce((acc, { value }) => acc + value, 0);
  const totalNumberOfSales =
    paymentsData?.reduce((acc, { value }) => acc + value, 0) +
    bookingsData?.reduce((acc, { value }) => acc + value, 0) +
    subscriptionsData?.reduce((acc, { value }) => acc + value, 0) +
    submissionsData?.reduce((acc, { value }) => acc + value, 0);

  const conversionData = data?.reduce((acc, { timestamp, value }) => {
    const numberOfPayments = paymentsData?.find(
      ({ timestamp: paymentTimestamp }) => paymentTimestamp === timestamp,
    )?.value;
    const numberOfBookings = bookingsData?.find(
      ({ timestamp: bookingTimestamp }) => bookingTimestamp === timestamp,
    )?.value;
    const numberOfSubscriptions = subscriptionsData?.find(
      ({ timestamp: subscriptionTimestamp }) => subscriptionTimestamp === timestamp,
    )?.value;
    const numberOfSubmissions = submissionsData?.find(
      ({ timestamp: submissionTimestamp }) => submissionTimestamp === timestamp,
    )?.value;

    const conversionRate = getConversionRate(
      numberOfPayments + numberOfBookings + numberOfSubscriptions + numberOfSubmissions,
      value,
    );

    return [
      ...acc,
      {
        timestamp: formatTimestamp(timestamp, unit, range),
        'Number of views': formatValue(timestamp, value),
        'Conversion rate': formatValue(timestamp, conversionRate),
      },
    ];
  }, []);

  const maxNumberOfVisits = data && Math.max(...data?.map(({ value }) => value), 0);
  const minNumberOfVisits = data && Math.min(...data?.map(({ value }) => value), 0);

  const maxConversionRate =
    conversionData &&
    Math.max(
      ...conversionData?.map(items =>
        isNumber(items['Conversion rate']) ? items['Conversion rate'] : 0,
      ),
      0,
    ) / 100;

  const minConversionRate =
    conversionData &&
    Math.min(
      ...conversionData?.map(items =>
        isNumber(items['Conversion rate']) ? items['Conversion rate'] : 0,
      ),
      0,
    ) / 100;

  const conversionRate = getConversionRate(totalNumberOfSales, totalVisits);

  return (
    <div>
      <TabGroup>
        <TabList className="flex gap-x-8 w-full mb-2">
          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoading}
                  label="Views"
                  tooltip={
                    <StatTooltip>The total number of times your pages were viewed.</StatTooltip>
                  }
                  value={numberFormatter(sumArray(conversionData, 'Number of views'))}
                />
              </TabLink>
            )}
          </Tab>

          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoading}
                  label="Conversion rate"
                  tooltip={
                    <StatTooltip>The percentage of visitors who completed a checkout.</StatTooltip>
                  }
                  value={percentageFormatter(conversionRate / 100)}
                />
              </TabLink>
            )}
          </Tab>
        </TabList>

        <StatBody>
          <TabPanels>
            <TabPanel>
              <StatChart
                isLoading={isLoading}
                data={conversionData}
                categories={['Number of views']}
                maxValue={maxNumberOfVisits}
                minValue={minNumberOfVisits}
              />
            </TabPanel>

            <TabPanel>
              <StatChart
                isLoading={isLoading}
                data={conversionData}
                categories={['Conversion rate']}
                maxValue={percentageFormatter(maxConversionRate)}
                minValue={percentageFormatter(minConversionRate)}
                valueFormatter={v => percentageFormatter(v / 100)}
              />
            </TabPanel>
          </TabPanels>
        </StatBody>
      </TabGroup>
    </div>
  );
}
